import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import RouteTransition from './components/RouteTransition';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import Spinner from './components/Spinner';

// Lazy load route components
const Landing = lazy(() => import('./pages/Landing'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const CompanySignup = lazy(() => import('./pages/CompanySignup'));
const OpenHome = lazy(() => import('./pages/OpenHome'));
const SchoolHome = lazy(() => import('./pages/SchoolHome'));
const CollegeHome = lazy(() => import('./pages/CollegeHome'));
const FeedPage = lazy(() => import('./pages/FeedPage'));
const CompanyRoute = lazy(() => import('./components/CompanyRoute'));
const CompanyHome = lazy(() => import('./pages/company/CompanyHome'));
const CollegeLayout = lazy(() => import('./components/college/CollegeLayout'));
const PlacementZoneLayout = lazy(() => import('./components/college/PlacementZoneLayout'));
const PlacementLayout = lazy(() => import('./pages/college/placement/PlacementLayout'));
const PlacementDiscover = lazy(() => import('./pages/college/placement/PlacementDiscover'));
const PlacementCompanies = lazy(() => import('./pages/college/placement/PlacementCompanies'));
const PlacementCompanyProfile = lazy(() => import('./pages/college/placement/PlacementCompanyProfile'));
const PlacementRooms = lazy(() => import('./pages/college/placement/PlacementRooms'));
const PlacementRoomDetail = lazy(() => import('./pages/college/placement/PlacementRoomDetail'));
const PlacementDiscussionDetail = lazy(() => import('./pages/college/placement/PlacementDiscussionDetail'));
const PlacementJobs = lazy(() => import('./pages/college/placement/PlacementJobs'));
const PlacementJobDetail = lazy(() => import('./pages/college/placement/PlacementJobDetail'));
const PlacementInternships = lazy(() => import('./pages/college/placement/PlacementInternships'));
const PlacementInternshipDetail = lazy(() => import('./pages/college/placement/PlacementInternshipDetail'));
const PlacementChallenges = lazy(() => import('./pages/college/placement/PlacementChallenges'));
const PlacementChallengeDetail = lazy(() => import('./pages/college/placement/PlacementChallengeDetail'));
const PlacementLeaderboard = lazy(() => import('./pages/college/placement/PlacementLeaderboard'));
const PlacementApplications = lazy(() => import('./pages/college/placement/PlacementApplications'));
const PlacementApplicationDetail = lazy(() => import('./pages/college/placement/PlacementApplicationDetail'));
const CollegeEvents = lazy(() => import('./pages/college/CollegeEvents'));
const EventDetail = lazy(() => import('./pages/college/EventDetail'));
const EventsHub = lazy(() => import('./pages/college/EventsHub'));
const CreateEventV2 = lazy(() => import('./pages/college/CreateEventV2'));
const EventDetailV2 = lazy(() => import('./pages/college/EventDetailRedesigned'));
const OrganizerDashboard = lazy(() => import('./pages/college/OrganizerDashboard'));
const Leaderboard = lazy(() => import('./pages/college/Leaderboard'));
const CollegeGroups = lazy(() => import('./pages/college/CollegeGroups'));
const CommunitiesPage = lazy(() => import('./pages/college/CommunitiesPage'));
const CommunityDetailPage = lazy(() => import('./pages/college/CommunityDetailPage'));
const GroupChat = lazy(() => import('./pages/college/GroupChat'));
const GroupSettings = lazy(() => import('./pages/college/GroupSettings'));
const CollegeMessages = lazy(() => import('./pages/college/CollegeMessages'));
const CollegeConnections = lazy(() => import('./pages/college/CollegeConnections'));
const UserProfile = lazy(() => import('./pages/college/UserProfile'));
const JobBoard = lazy(() => import('./pages/college/JobBoard'));
const JobDetail = lazy(() => import('./pages/college/JobDetail'));
const InternshipBoard = lazy(() => import('./pages/college/InternshipBoard'));
const InternshipDetail = lazy(() => import('./pages/college/InternshipDetail'));
const MyApplications = lazy(() => import('./pages/college/MyApplications'));
const PlacementHome = lazy(() => import('./pages/college/PlacementHome'));
const TrendingHashtags = lazy(() => import('./pages/college/TrendingHashtags'));
const HashtagPosts = lazy(() => import('./pages/college/HashtagPosts'));
const BlogPostDetail = lazy(() => import('./pages/college/BlogPostDetail'));
const StudentSettings = lazy(() => import('./pages/college/settings/StudentSettings'));

// Protected Route Component
const ProtectedRoute = ({ children, requiredMode }) => {
  const { user, company, mode, initializing, isPreviewMode } = useAuth();

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-page dark:bg-[#0b0b15]">
        <div className="text-gray-600 dark:text-neutral-400">Loading...</div>
      </div>
    );
  }

  // Allow access if user exists OR if company exists (for preview mode)
  if (!user && !company) {
    return <Navigate to="/login" replace />;
  }

  // If a specific mode is required, check if user's mode matches
  // Skip mode check if in preview mode (company viewing as student)
  if (requiredMode && mode !== requiredMode && !isPreviewMode) {
    // Redirect to user's actual mode home
    return <Navigate to={`/${mode}`} replace />;
  }

  return children;
};

// Public Route Component (redirects to mode-specific home if already authenticated)
const PublicRoute = ({ children }) => {
  const { user, company, mode, initializing } = useAuth();

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-page dark:bg-[#0b0b15]">
        <div className="text-gray-600 dark:text-neutral-400">Loading...</div>
      </div>
    );
  }

  if (user && mode) {
    if (mode === 'college') {
      const collegeId = localStorage.getItem('collegeId');
      return <Navigate to={collegeId ? `/college/${collegeId}/feed` : '/college'} replace />;
    }
    return <Navigate to={`/${mode}`} replace />;
  }

  if (company && mode === 'company') {
    return <Navigate to="/company/dashboard" replace />;
  }

  return children;
};

// Landing page route component
const LandingRoute = () => {
  const { user, mode } = useAuth();
  
  if (user && mode) {
    if (mode === 'college') {
      const collegeId = localStorage.getItem('collegeId');
      return <Navigate to={collegeId ? `/college/${collegeId}/feed` : '/college'} replace />;
    }
    return <Navigate to={`/${mode}`} replace />;
  }
  
  return <Landing />;
};

// Legacy route redirect component
const LegacyCollegeRedirect = ({ path = 'feed' }) => {
  const { collegeId } = useAuth();
  const location = window.location;
  
  if (!collegeId) {
    return <Navigate to="/college" replace />;
  }
  
  // Extract userId from path if present (for profile routes)
  const pathParts = location.pathname.split('/');
  const userId = pathParts[pathParts.length - 1];
  
  // If path is profile and we have a userId that's not 'profile', append it
  if (path === 'profile' && userId && userId !== 'profile') {
    return <Navigate to={`/college/${collegeId}/profile/${userId}`} replace />;
  }
  
  return <Navigate to={`/college/${collegeId}/${path}`} replace />;
};

function AppContent() {
  const { user, mode } = useAuth();

  return (
    <div className="min-h-screen bg-background-page dark:bg-[#0b0b15]">
      {user && mode !== 'college' && <Navbar />}
      <RouteTransition>
        <Routes>
          {/* Landing Page */}
          <Route path="/" element={<LandingRoute />} />

        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <Signup />
            </PublicRoute>
          }
        />
        <Route
          path="/company/signup"
          element={
            <PublicRoute>
              <CompanySignup />
            </PublicRoute>
          }
        />

        {/* Company Routes - MUST come before /college/* to avoid route conflicts */}
        <Route
          path="/company/*"
          element={
            <CompanyRoute>
              <Suspense
                fallback={
                  <div className="min-h-screen flex items-center justify-center bg-background-page dark:bg-[#0b0b15]">
                    <div className="text-gray-600 dark:text-neutral-400">Loading...</div>
                  </div>
                }
              >
                <CompanyHome />
              </Suspense>
            </CompanyRoute>
          }
        />

        {/* New Placement Zone Routes - rendered inside CollegeLayout below */}
        {/* REMOVED standalone placement route - now nested in CollegeLayout */}

        {/* Placement Zone Routes with PlacementZoneLayout - No left navigation sidebar */}
        {/* IMPORTANT: These routes MUST come BEFORE CollegeLayout routes to match first */}
        <Route
          path="/college/:collegeId"
          element={
            <ProtectedRoute>
              <PlacementZoneLayout />
            </ProtectedRoute>
          }
        >
          <Route path="placement" element={<PlacementHome />} />
          <Route path="jobs" element={<JobBoard />} />
          <Route path="jobs/:jobId" element={<JobDetail />} />
          <Route path="internships" element={<InternshipBoard />} />
          <Route path="internships/:internshipId" element={<InternshipDetail />} />
          <Route path="applications" element={<MyApplications />} />
        </Route>

        {/* Unified College Routes with Layout - Accessible by both students and companies */}
        <Route
          path="/college/:collegeId"
          element={
            <ProtectedRoute>
              <CollegeLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="feed" replace />} />
          <Route path="feed" element={<FeedPage />} />
          <Route path="events" element={<EventsHub />} />
          <Route path="events/create" element={<CreateEventV2 />} />
          <Route path="events/:eventId" element={<EventDetailV2 />} />
          <Route path="events/:eventId/organize" element={<OrganizerDashboard />} />
          <Route path="leaderboard" element={<Leaderboard />} />
          <Route path="communities" element={<CommunitiesPage />} />
          <Route path="communities/:communityId" element={<CommunityDetailPage />} />
          <Route path="communities/:communityId/chat" element={<GroupChat />} />
          <Route path="communities/:communityId/settings" element={<GroupSettings />} />
          <Route path="messages" element={<CollegeMessages />} />
          <Route path="messages/:userId" element={<CollegeMessages />} />
          <Route path="connections" element={<CollegeConnections />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="profile/:userId" element={<UserProfile />} />
          <Route path="settings/*" element={<StudentSettings />} />
          <Route path="trending" element={<TrendingHashtags />} />
          <Route path="hashtag/:hashtag" element={<HashtagPosts />} />
          <Route path="post/:postId" element={<BlogPostDetail />} />

          {/* Placement Zone Routes - nested inside CollegeLayout */}
          <Route path="placement" element={<PlacementLayout />}>
            <Route index element={<Navigate to="discover" replace />} />
            <Route path="discover" element={<PlacementDiscover />} />
            <Route path="companies" element={<PlacementCompanies />} />
            <Route path="companies/:companyId" element={<PlacementCompanyProfile />} />
            <Route path="rooms" element={<PlacementRooms />} />
            <Route path="rooms/:roomId" element={<PlacementRoomDetail />} />
            <Route path="rooms/:roomId/discussions/:discussionId" element={<PlacementDiscussionDetail />} />
            <Route path="jobs" element={<PlacementJobs />} />
            <Route path="jobs/:jobId" element={<PlacementJobDetail />} />
            <Route path="internships" element={<PlacementInternships />} />
            <Route path="internships/:internshipId" element={<PlacementInternshipDetail />} />
            <Route path="challenges" element={<PlacementChallenges />} />
            <Route path="challenges/:challengeId" element={<PlacementChallengeDetail />} />
            <Route path="leaderboard" element={<PlacementLeaderboard />} />
            <Route path="applications" element={<PlacementApplications />} />
            <Route path="applications/:applicationId" element={<PlacementApplicationDetail />} />
          </Route>
        </Route>

        {/* Protected Mode-Specific Routes */}
        <Route
          path="/open"
          element={
            <ProtectedRoute requiredMode="open">
              <OpenHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/school"
          element={
            <ProtectedRoute requiredMode="school">
              <SchoolHome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/college/*"
          element={
            <ProtectedRoute requiredMode="college">
              <CollegeHome />
            </ProtectedRoute>
          }
        />

        {/* Legacy College Route Redirects - Redirect to unified routes */}
        <Route path="/college" element={<LegacyCollegeRedirect path="feed" />} />
        <Route path="/college/events" element={<LegacyCollegeRedirect path="events" />} />
        <Route path="/college/leaderboard" element={<LegacyCollegeRedirect path="leaderboard" />} />
        <Route path="/college/groups" element={<LegacyCollegeRedirect path="communities" />} />
        <Route path="/college/communities" element={<LegacyCollegeRedirect path="communities" />} />
        <Route path="/college/messages" element={<LegacyCollegeRedirect path="messages" />} />
        <Route path="/college/connections" element={<LegacyCollegeRedirect path="connections" />} />
        <Route path="/college/profile" element={<LegacyCollegeRedirect path="profile" />} />
        <Route path="/college/profile/:userId" element={<LegacyCollegeRedirect path="profile" />} />
        <Route path="/college/placement" element={<LegacyCollegeRedirect path="placement/discover" />} />
        <Route path="/college/jobs" element={<LegacyCollegeRedirect path="jobs" />} />
        <Route path="/college/internships" element={<LegacyCollegeRedirect path="internships" />} />
        <Route path="/college/applications" element={<LegacyCollegeRedirect path="applications" />} />
        <Route path="/college/trending" element={<LegacyCollegeRedirect path="trending" />} />

        {/* 404 Route */}
        <Route
          path="*"
          element={
            <div className="min-h-screen flex items-center justify-center bg-background-page dark:bg-[#0b0b15]">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-neutral-50 mb-4">404</h1>
                <p className="text-gray-600 dark:text-neutral-400">Page not found</p>
              </div>
            </div>
          }
        />
      </Routes>
    </RouteTransition>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <SocketProvider>
            <AppContent />
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
